import type { Ref } from 'react'

export interface CodeInputProps {
  /**
   * 'totp' is the six digits from an authenticator app; 'recovery' is one of
   * the hyphenated fallback codes. They differ in keyboard, length, casing,
   * and letter-spacing, and getting any of those wrong is felt immediately by
   * someone typing under time pressure with a phone in their other hand.
   */
  mode: 'totp' | 'recovery'
  value: string
  onChange: (value: string) => void
  id?: string
  ref?: Ref<HTMLInputElement>
  'aria-describedby'?: string
  'aria-invalid'?: boolean
  'aria-required'?: boolean
}

/** The number of characters a complete code of each kind has. */
export const CODE_LENGTH = { totp: 6, recovery: 11 } as const

/** Whether the value looks complete enough to be worth submitting. */
export function isCodeComplete(mode: 'totp' | 'recovery', value: string): boolean {
  return mode === 'totp' ? value.length === CODE_LENGTH.totp : value.replace('-', '').length >= 10
}

/**
 * CodeInput — the one-time code field.
 *
 * Input is normalised as it is typed rather than validated on submit: digits
 * only for TOTP, upper case for recovery codes. The server normalises the
 * same way, so a pasted code with stray spaces or lower case still works —
 * but the user should see it land correctly instead of being told off for
 * pasting exactly what was on their screen.
 */
export function CodeInput({ mode, value, onChange, ref, ...aria }: CodeInputProps) {
  return (
    <input
      {...aria}
      ref={ref}
      className={`lg-input lg-code-input lg-code-input--${mode}`}
      // numeric brings up the digit keypad on mobile; recovery codes are
      // alphanumeric and need the full keyboard.
      inputMode={mode === 'totp' ? 'numeric' : 'text'}
      // Lets iOS and Android offer the code straight from the authenticator
      // sheet rather than making the user switch apps and memorise it.
      autoComplete="one-time-code"
      spellCheck={false}
      autoCapitalize="characters"
      maxLength={mode === 'totp' ? CODE_LENGTH.totp : 12}
      value={value}
      onChange={(e) => {
        const next =
          mode === 'totp'
            ? e.target.value.replace(/\D/g, '')
            : e.target.value.toUpperCase().replace(/[^A-Z0-9-]/g, '')
        onChange(next)
      }}
    />
  )
}
